import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Users2, Compass, HeartHandshake, Rocket, Target, MapPin, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";

const PILLARS = [
  { icon: Target, title: "Visibility over paperwork", body: "The register, heatmap and appetite live at the top of the app - not buried five clicks deep behind a workflow builder. If it can't be answered in ten seconds, we build it until it can." },
  { icon: ShieldCheck, title: "Security by default", body: "TOTP is mandatory. AES-256 at rest. Hash-chained audit trail. Nothing important is a paid upsell - because the day you need it isn't the day you should upgrade." },
  { icon: HeartHandshake, title: "Every tenant gets an advisor", body: "You get a real human who knows your register - not a chatbot and not a shared Slack. Sessions, notes and actions land inside the app so the whole team benefits, not just the person on the call." },
];

const TIMELINE = [];

const PRINCIPLES = [
  "Encryption is a floor, not a ceiling.",
  "Every mutation appends an audit event - tamper-evident.",
  "Reports must reproduce byte-identical.",
  "Two-factor auth is table stakes.",
  "Advisors are humans, not chatbots.",
];

export default function About() {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = "About RiskEye · Risk register & M365 assessment by seQure";
    const desc = document.querySelector('meta[name="description"]');
    const prevDesc = desc?.getAttribute("content");
    desc?.setAttribute("content", "About RiskEye and seQure — the practitioners behind a digital risk register and Microsoft 365 assessment platform with action tracking. Visibility over paperwork, security by default, every tenant gets a human advisor.");
    const link = document.querySelector('link[rel="canonical"]');
    const prevHref = link?.getAttribute("href");
    link?.setAttribute("href", "https://riskeye.io/about");
    return () => {
      document.title = prevTitle;
      if (desc && prevDesc != null) desc.setAttribute("content", prevDesc);
      if (link && prevHref != null) link.setAttribute("href", prevHref);
    };
  }, []);
  return (
    <div className="min-h-screen bg-[#FBFBF9] text-slate-900 font-sans" data-testid="about-page">
      {/* Nav */}
      <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-md border-b border-slate-200/70">
        <div className="max-w-6xl mx-auto flex items-center justify-between px-6 py-3.5">
          <Link to="/" className="flex items-center gap-2.5" data-testid="about-brand">
            <div className="h-8 w-8 rounded-md bg-gradient-to-br from-rose-500 via-amber-500 to-emerald-500 p-[1.5px]">
              <div className="h-full w-full rounded-[5px] bg-[#050B1F] flex items-center justify-center">
                <div className="h-1.5 w-1.5 rounded-full bg-rose-400" />
              </div>
            </div>
            <div className="leading-tight">
              <div className="font-display font-bold text-[19px] tracking-tight leading-none">RiskEye</div>
              <div className="text-[9.5px] font-mono uppercase tracking-[0.22em] text-slate-500 mt-0.5">by seQure</div>
            </div>
          </Link>
          <Link to="/" className="inline-flex items-center gap-1.5 text-[13.5px] text-slate-600 hover:text-slate-900" data-testid="about-back">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to home
          </Link>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none" style={{
          background: "radial-gradient(45% 55% at 85% 10%, rgba(6, 182, 212, 0.10), transparent 70%), radial-gradient(45% 45% at 5% 95%, rgba(244, 63, 94, 0.08), transparent 70%)",
        }} />
        <div className="relative max-w-4xl mx-auto px-6 pt-16 md:pt-24 pb-10">
          <div className="inline-flex items-center gap-2 bg-white border border-slate-200 rounded-full px-3 py-1 text-[11.5px] font-mono uppercase tracking-[0.18em] text-slate-500 shadow-sm mb-6">
            <Compass className="h-3 w-3 text-cyan-600" /> About RiskEye
          </div>
          <h1 className="font-display font-bold text-[42px] md:text-[54px] leading-[1.05] tracking-tight text-slate-900">
            We build the risk software <br/>
            <span className="bg-gradient-to-br from-cyan-700 via-cyan-600 to-emerald-600 bg-clip-text text-transparent">
              we wanted, when we were in your seat.
            </span>
          </h1>
          <p className="mt-6 text-[16px] md:text-[17px] text-slate-600 leading-relaxed max-w-2xl">
            RiskEye is built by <span className="font-semibold text-slate-800">seQure</span> - a small, opinionated team of practitioners who ran risk programmes out of spreadsheets, SharePoint and Trello, and got tired of it. We started RiskEye to give risk teams a real tool that answers "what's our top risk right now?" in under ten seconds, and hands boards a byte-identical PDF they can defend in front of an auditor.
          </p>
        </div>
      </section>

      {/* Pillars */}
      <section className="max-w-6xl mx-auto px-6 pt-6 pb-16">
        <div className="grid md:grid-cols-3 gap-4" data-testid="about-pillars">
          {PILLARS.map((p) => (
            <div key={p.title} className="rounded-xl border border-slate-200 bg-white p-6 hover:border-slate-900 hover:shadow-[0_12px_30px_-16px_rgba(15,23,42,0.4)] transition-all">
              <div className="h-11 w-11 rounded-md bg-gradient-to-br from-slate-900 to-slate-700 flex items-center justify-center text-white mb-4">
                <p.icon className="h-5 w-5" />
              </div>
              <div className="font-display font-semibold text-[18px] tracking-tight text-slate-900">{p.title}</div>
              <p className="text-[13.5px] text-slate-600 leading-relaxed mt-2">{p.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Our story - short blurb */}
      <section className="bg-slate-50 border-y border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-16">
          <div className="text-[10.5px] uppercase tracking-[0.28em] font-mono text-emerald-700 mb-3">Our story</div>
          <h2 className="font-display font-bold text-[30px] md:text-[36px] tracking-tight leading-tight">Small team. Serious about risk.</h2>
          <p className="mt-5 text-[16px] text-slate-600 leading-relaxed max-w-2xl">
            seQure was founded on the belief that the day-to-day job of a risk team is <em>visibility</em>, not compliance theatre. We build RiskEye alongside a handful of design-partner tenants - every feature ships when a real risk manager tells us it earned its space in the sidebar. If it isn't in the product yet, it's because we haven't heard the right story for it.
          </p>
        </div>
      </section>

      {/* Principles */}
      <section className="max-w-4xl mx-auto px-6 py-20">
        <div className="text-[10.5px] uppercase tracking-[0.28em] font-mono text-rose-600 mb-3">Security first</div>
        <h2 className="font-display font-bold text-[30px] md:text-[36px] tracking-tight leading-tight">Five commitments we ship against.</h2>
        <div className="mt-8 space-y-3">
          {PRINCIPLES.map((p, i) => (
            <div key={p} className="flex items-start gap-4 rounded-xl border border-slate-200 bg-white px-5 py-4">
              <div className="h-8 w-8 rounded-md bg-slate-900 text-white flex items-center justify-center font-mono text-[13px] font-semibold shrink-0">{String(i + 1).padStart(2, "0")}</div>
              <div className="text-[15px] text-slate-800 leading-relaxed">{p}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Trust + Contact */}
      <section className="max-w-6xl mx-auto px-6 pb-24">
        <div className="grid md:grid-cols-2 gap-4">
          <div className="rounded-2xl bg-[#050B1F] p-8 md:p-10 text-slate-100 relative overflow-hidden">
            <div className="absolute inset-0 opacity-40 pointer-events-none" style={{
              background: "radial-gradient(45% 45% at 15% 15%, rgba(6, 182, 212, 0.20), transparent 70%), radial-gradient(35% 35% at 90% 90%, rgba(16, 185, 129, 0.14), transparent 70%)",
            }} />
            <div className="relative">
              <div className="text-[10.5px] font-mono uppercase tracking-[0.28em] text-cyan-300 mb-3">Trust posture</div>
              <div className="font-display font-bold text-[22px] tracking-tight text-white">Encryption, audit, 2FA - on by default.</div>
              <p className="mt-3 text-[13.5px] text-slate-300 leading-relaxed">TLS 1.3, AES-256 at rest, Fernet-encrypted TOTP secrets, HMAC-hashed invitation tokens and a SHA-256 hash-chained audit trail. The full matrix is public.</p>
              <Link to="/trust" className="mt-6 inline-flex items-center gap-1.5 bg-white text-slate-900 hover:bg-slate-100 font-medium text-[13.5px] px-4 py-2 rounded-md transition-colors" data-testid="about-trust-link">
                See the trust matrix →
              </Link>
            </div>
          </div>
          <div className="rounded-2xl bg-white border border-slate-200 p-8 md:p-10">
            <div className="text-[10.5px] font-mono uppercase tracking-[0.28em] text-slate-400 mb-3">Talk to us</div>
            <div className="font-display font-bold text-[22px] tracking-tight text-slate-900">One inbox. One team.</div>
            <p className="mt-3 text-[13.5px] text-slate-600 leading-relaxed">Whether you're comparing tools, kicking the tyres or already a customer - you'll get an answer from a human, usually the same day.</p>
            <div className="mt-6 space-y-2.5">
              <a href="mailto:hello@riskeye.io" className="flex items-center gap-3 text-[14px] text-slate-800 hover:text-cyan-700 group" data-testid="about-email">
                <div className="h-9 w-9 rounded-md bg-slate-100 flex items-center justify-center text-slate-600 group-hover:bg-cyan-50 group-hover:text-cyan-700 transition-colors"><Mail className="h-4 w-4" /></div>
                <div>
                  <div className="font-medium">hello@riskeye.io</div>
                  <div className="text-[11.5px] text-slate-500">Support, sales, security disclosures - anything.</div>
                </div>
              </a>
              <a href="https://calendly.com/sequrenz/30min" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-[14px] text-slate-800 hover:text-cyan-700 group" data-testid="about-calendly">
                <div className="h-9 w-9 rounded-md bg-slate-100 flex items-center justify-center text-slate-600 group-hover:bg-cyan-50 group-hover:text-cyan-700 transition-colors"><Rocket className="h-4 w-4" /></div>
                <div>
                  <div className="font-medium">Book a 30-min walk-through</div>
                  <div className="text-[11.5px] text-slate-500">Bring your top 10 risks - we'll seed a private tenant.</div>
                </div>
              </a>
              <div className="flex items-center gap-3 text-[14px] text-slate-800">
                <div className="h-9 w-9 rounded-md bg-slate-100 flex items-center justify-center text-slate-600"><MapPin className="h-4 w-4" /></div>
                <div>
                  <div className="font-medium">Global · APAC-headquartered</div>
                  <div className="text-[11.5px] text-slate-500">seQure operates in AU / NZ time zones - with customers on four continents.</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer strip */}
      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-6xl mx-auto px-6 py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-[11.5px] font-mono uppercase tracking-[0.22em] text-slate-400">
          <span>© {new Date().getFullYear()} RiskEye · by seQure</span>
          <div className="flex items-center gap-5">
            <Link to="/" className="hover:text-slate-800">Home</Link>
            <Link to="/trust" className="hover:text-slate-800">Trust</Link>
            <Link to="/changelog" className="hover:text-slate-800">Changelog</Link>
            <a href="mailto:hello@riskeye.io" className="hover:text-slate-800">Contact</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
